import { cn } from "@/lib/utils";

/**
 * Layout-matching loading skeletons. Use for page/list/table loading; reserve the Loader2
 * spinner for in-flight button states only.
 *
 * <LoadingSkeleton variant="table" rows={8} cols={5} />
 *
 * props:
 *  - variant: 'table' | 'cards' | 'detail' | 'form' | 'stat-row'
 *  - rows?, cols?
 */
const Bar = ({ className }) => <div className={cn("animate-pulse rounded-md bg-slate-200", className)} />;

export function LoadingSkeleton({ variant = "cards", rows = 6, cols = 4, className }) {
  if (variant === "stat-row") {
    return (
      <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4", className)}>
        {Array.from({ length: cols }).map((_, i) => (
          <div key={i} className="rounded-xl border bg-white p-5 shadow-sm">
            <Bar className="h-3 w-24" />
            <Bar className="mt-3 h-8 w-20" />
          </div>
        ))}
      </div>
    );
  }

  if (variant === "table") {
    return (
      <div className={cn("rounded-xl border bg-white overflow-hidden", className)}>
        <div className="hidden md:block">
          <div className="flex gap-4 border-b bg-slate-50 px-4 py-3">
            {Array.from({ length: cols }).map((_, i) => (
              <Bar key={i} className="h-3 flex-1" />
            ))}
          </div>
          {Array.from({ length: rows }).map((_, r) => (
            <div key={r} className="flex gap-4 border-b px-4 py-4 last:border-0">
              {Array.from({ length: cols }).map((_, c) => (
                <Bar key={c} className="h-4 flex-1" />
              ))}
            </div>
          ))}
        </div>
        <div className="md:hidden divide-y">
          {Array.from({ length: rows }).map((_, r) => (
            <div key={r} className="space-y-2 p-4">
              <Bar className="h-4 w-1/2" />
              <Bar className="h-3 w-3/4" />
              <Bar className="h-3 w-2/3" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "detail") {
    return (
      <div className={cn("space-y-4", className)}>
        <Bar className="h-7 w-1/3" />
        <div className="rounded-xl border bg-white p-6 space-y-3 shadow-sm">
          {Array.from({ length: rows }).map((_, i) => (
            <div key={i} className="flex gap-4">
              <Bar className="h-4 w-32" />
              <Bar className="h-4 flex-1" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === "form") {
    return (
      <div className={cn("space-y-4", className)}>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <Bar className="h-3 w-24" />
            <Bar className="h-10 w-full" />
          </div>
        ))}
      </div>
    );
  }

  // cards (default)
  return (
    <div className={cn("grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4", className)}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="rounded-xl border bg-white p-5 space-y-3 shadow-sm">
          <Bar className="h-4 w-2/3" />
          <Bar className="h-3 w-full" />
          <Bar className="h-3 w-4/5" />
          <Bar className="h-8 w-1/2" />
        </div>
      ))}
    </div>
  );
}

export default LoadingSkeleton;
